@use '../../../scss/utility';
@use '../modal.utility.scss' as modalUtility;

:host {
  display: block;
  overflow: hidden;
  min-height: 0;
}

.inner {
  display: none;
  overflow: hidden;

  &.is-visible {
    display: flex;
    flex-direction: column;
    max-height: 100vh;
  }

  &.is-entering {
    position: absolute; // position the element above the expanding previous element
    inset-block-start: modalUtility.$modal-header-height;
    display: block;
    width: 100%;
  }
}

.is-entering,
.is-leaving {
  .content,
  .footer {
    opacity: 0;
  }
}

.is-full-height {
  height: 100dvh;

  @include utility.breakpoint-from(mq4) {
    height: auto;
  }
}

.height-transition {
  transition: height modalUtility.$animation-duration var(--wb-move);
}

.header {
  display: flex;
  align-items: center;
  height: modalUtility.$modal-header-height;
  border-block-end: var(--modal-border-weight) solid var(--modal-border-color);
  padding: utility.wb-pixel-to-rem(11px);
  box-sizing: border-box;

  &.is-hidden {
    transform: translateY(-100%);
  }
  &.is-visible {
    transform: translateY(0%);
  }
}

.footer {
  display: flex;
  flex-direction: column;
  justify-content: end;
  box-sizing: border-box;
  @include utility.wb-spacing('padding', 's xs');

  @include utility.breakpoint-from(mq4) {
    flex-direction: row;
  }

  &.is-hidden {
    transform: translateY(100%);
  }
  &.is-visible {
    transform: translateY(0%);
  }
}

.close-button {
  margin-inline-start: auto;
}

.header,
.footer {
  transition: transform modalUtility.$animation-duration var(--wb-fade);
}

.content {
  color: var(--modal-text-body-color);
  display: block;
  overflow: auto;
  box-sizing: border-box;

  // Fix for Firefox padding
  &::after {
    content: '';
    display: block;
    width: 100%;
    height: var(--content-after-height);
  }

  &.fade-in {
    animation: wbModalFadeIn modalUtility.$animation-duration var(--wb-fade) forwards;
  }
  &.fade-out {
    animation: wbModalFadeOut modalUtility.$animation-duration var(--wb-fade) forwards;
  }
}

.headline {
  @include utility.wb-spacing('margin-block-end', 'xxs');
  display: block;
  color: var(--modal-text-headline-color);
  margin-block-start: 0;
}

:host([level='3']) {
  content {
    &.fade-out {
      animation: wbModalFadeOutToLeft modalUtility.$animation-duration var(--wb-fade);
    }
    &.fade-in {
      animation: wbModalFadeInFromRight modalUtility.$animation-duration var(--wb-fade);
    }
  }
}

:host([is-rtl][level='3']) {
  .content {
    &.fade-out {
      animation: wbModalFadeOutToRight modalUtility.$animation-duration var(--wb-fade);
    }
    &.fade-in {
      animation: wbModalFadeInFromLeft modalUtility.$animation-duration var(--wb-fade);
    }
  }
}

::slotted(.wb-button[slot='footer']:not(:last-child)) {
  @include utility.wb-spacing('margin-block-end', 'xs');

  @include utility.breakpoint-from(mq4) {
    margin-block-end: 0;
    @include utility.wb-spacing('margin-inline-end', 's');
  }
}

:host([variant='edit']),
:host([variant='edit-mobile-fullscreen']) {
  .content {
    @include utility.wb-spacing('padding', 'xs xs 0 xs');

    @include utility.breakpoint-from(mq4) {
      @include utility.wb-spacing('padding', 's xs 0 xs');
      max-height: modalUtility.$edit-modal-content-max-height;
    }

    &::after {
      height: var(--wb-spacing-m);
    }
  }

  .has-footer {
    .content {
      max-height: modalUtility.$edit-modal-content-max-height;
    }
  }

  .footer {
    background-color: var(--modal-background-color);
    @include utility.wb-spacing('padding', '#{utility.wb-pixel-to-rem(12px)} xs');
    inset-block-end: 0;
    width: 100%;
    position: relative;
  }
}

:host([variant='edit-mobile-fullscreen']) {
  @include utility.breakpoint-to(mq3) {
    /* make sure footer sticks to the bottom */
    .inner {
      height: 100dvh;
    }

    .footer {
      margin-block-start: auto;
    }

    .has-footer {
      .content {
        max-height: 100%;
      }
    }
  }
}

:host([variant='info']) {
  .content {
    height: 100%;
    @include utility.wb-spacing('padding', 's xs 0 xs');
    overflow: auto;

    &::after {
      height: var(--wb-spacing-l);
    }
  }
}

:host([variant='prompt']) {
  .content {
    @include utility.wb-spacing('padding', 'xs xs 0 xs');

    &::after {
      height: var(--wb-spacing-xs);
    }
  }

  .footer {
    @include utility.wb-spacing('padding', 's xs');
    background-color: var(--modal-background-color);
    border-block-start: var(--wb-modal-border-weight) solid var(--modal-border-color);
  }
}

@keyframes wbModalSlideToRight {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(40px);
  }
}

@keyframes wbModalFadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes wbModalFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes wbModalFadeOutToLeft {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-40px);
  }
}

@keyframes wbModalFadeOutToRight {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(40px);
  }
}

@keyframes wbModalFadeInFromRight {
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0px);
  }
}

@keyframes wbModalFadeInFromLeft {
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateY(0px);
  }
}
